"use client";

import mapboxgl from "mapbox-gl";
import { useEffect, useRef } from "react";
import { useLoadedMap } from "../MapContext";

/** Widest the bar may draw before it rounds down to the next nice distance. */
const MAX_WIDTH = 100;

/**
 * The scale bar, hosted in the React overlay rather than in mapbox's own stack.
 *
 * Mapbox positions its controls inside the map container, which is the whole
 * viewport — so a bar it centred sat 200px left of the centre of the Visible
 * Area. The overlay is a flex sibling of the sidebar, so putting the bar there
 * centres it on the Visible Area with no measuring and no imperative transform,
 * which is what the legacy app needed one for.
 *
 * It is still a mapbox control — it has to be, to read distances off the
 * projection — just added by hand: `onAdd` returns the element and wires the map
 * listener that keeps it up to date, which is all `addControl` would have done
 * before appending it to a container of its own choosing.
 */
export function MapScale() {
  const map = useLoadedMap();
  const hostRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!map || !host) return;

    const control = new mapboxgl.ScaleControl({ maxWidth: MAX_WIDTH });
    host.appendChild(control.onAdd(map));

    return () => control.onRemove();
  }, [map]);

  return (
    <div
      ref={hostRef}
      className="pointer-events-none bg-white/40 p-2.5 pb-4 rounded-t-xl w-32 backdrop-blur-lg"
    />
  );
}
